/**
 * 人工提问 · 判定卡 04（PR3 升级）。
 * 刀 0 从 Loop 人工提问拷贝起步（来源见 components/plan-lab/COPIED_FROM.json），PR3 按判定卡改：
 * 一轮接 A-11（E-01）；计划来源固定为规则计划器（10-14 起注入计划不再提供：拿答案只能作上限对照，不计成绩）；
 * 挂起状态改读 plan 计划状态（事项进度含未激活事项及激活时机）；焦点单 · 焦点品保留（D-28 4-1）读会话状态 focus；
 * 三栏写死 h-[68vh] → 工作区吃满剩余高度（plan-workfill）；裸 input → ui/Input；11 号字 → text-xs；
 * 发送失败红字 ＋「重试」；客户列表加载失败 → 下拉置灰写原因；中栏轨迹读该轮运行记录详情（A-22 批 1 子集）。
 * PR5：三栏换成运行轨迹抽屉同一套（按层 ＋ 按事项分组，`PlanTraceColumns`）；预检取输入框当前内容，
 * 首轮消息还没填时原话片段（V-2）不判剔除、提示「首轮消息未填，原话片段到发送时再核」（线上验收问题 1）。
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select as StyledSelect } from "@/components/ui/select";
import {
	type PlanRecordDetail,
	type PlanTurnView,
	fetchPlanRecordDetail,
	planErrorMessage,
	useCatalog,
	usePlanTurn,
} from "@/lib/api/plan-lab";
import { RESUME_ACTION_ZH, STOP_REASON_ZH, zh } from "../kit/plan-labels";
import type { DrawerTurn } from "../trace-drawer/execution-drawer";
import { PlanTraceColumns } from "../trace-drawer/plan-trace-drawer";
import { PlanStatus } from "./plan-status";

type TimelineEntry = {
	msg: string;
	turn: PlanTurnView;
	recordId: string;
	/** undefined ＝ 拉取中；null ＝ 没拉到（detailErr 写原因） */
	detail?: PlanRecordDetail | null;
	detailErr?: string | null;
};

function toChatTurn(x: TimelineEntry): DrawerTurn {
	const t = x.turn;
	return {
		msg: x.msg,
		reply: t.reply,
		blocks: t.blocks,
		resume:
			t.resume_action && !["none", "new"].includes(t.resume_action)
				? zh(RESUME_ACTION_ZH, t.resume_action)
				: undefined,
		stop: zh(STOP_REASON_ZH, t.stop_reason),
		committed: t.tool_calls.some((c) => c.write_class === "committed"),
	};
}

const MIN_WORK_H = 420;
const BOTTOM_GAP = 24;

/** 工作区吃满剩余高度：视口高 − 工作区顶 − 底部留白，最小 420（ref 03 §6，L6） */
function useFillHeight(watch: React.RefObject<HTMLElement | null>) {
	const ref = useRef<HTMLDivElement | null>(null);
	const [h, setH] = useState(MIN_WORK_H);
	const measure = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const top = el.getBoundingClientRect().top + window.scrollY;
		setH(Math.max(MIN_WORK_H, Math.floor(window.innerHeight - top - BOTTOM_GAP)));
	}, []);
	useLayoutEffect(() => {
		measure();
		window.addEventListener("resize", measure);
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (ro && watch.current) ro.observe(watch.current);
		return () => {
			window.removeEventListener("resize", measure);
			ro?.disconnect();
		};
	}, [measure, watch]);
	return { ref, height: h };
}

export function PlaygroundTab({ hasKey }: { hasKey: boolean }) {
	const turn = usePlanTurn();
	const cat = useCatalog();
	const seedCustomers = cat.data?.seed.customers ?? [];
	const [customer, setCustomer] = useState("cust_demo");
	const [state, setState] = useState<Record<string, unknown> | null>(null);
	const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
	const [msg, setMsg] = useState("");
	const [err, setErr] = useState<string | null>(null);
	const [failedMsg, setFailedMsg] = useState<string | null>(null);
	const [pendingMsg, setPendingMsg] = useState<string | null>(null); // 已发出、正在调模型(乐观气泡)
	const [focus, setFocus] = useState(0);
	const toolbarRef = useRef<HTMLDivElement | null>(null);
	const work = useFillHeight(toolbarRef);
	const started = state !== null;
	const busy = turn.isPending;
	const last = timeline.at(-1)?.turn ?? null;

	function reset(nextCustomer?: string) {
		setState(null);
		setTimeline([]);
		setMsg("");
		setErr(null);
		setFailedMsg(null);
		setPendingMsg(null);
		setFocus(0);
		if (nextCustomer) setCustomer(nextCustomer);
	}

	async function send(override?: string) {
		const text = (override ?? msg).trim();
		if (!text || busy) return;
		setErr(null);
		setFailedMsg(null);
		setMsg("");
		setPendingMsg(text); // 乐观:用户气泡立即出现 + loading,不等结果
		setFocus(timeline.length);
		try {
			const d = await turn.mutateAsync({
				state,
				message: text,
				customer_id: started ? undefined : customer,
			});
			setState(d.state);
			setTimeline((t) => [...t, { msg: text, turn: d.turn, recordId: d.record_id }]);
			loadDetail(d.record_id);
		} catch (e) {
			setErr(planErrorMessage(e));
			setFailedMsg(text);
			setFocus((f) => Math.max(0, Math.min(f, timeline.length - 1)));
		} finally {
			setPendingMsg(null);
		}
	}

	/** 中栏轨迹：拉该轮运行记录详情（拉不到只少轨迹、给重试，不影响对话） */
	function loadDetail(recordId: string) {
		setTimeline((t) =>
			t.map((x) => (x.recordId === recordId ? { ...x, detail: undefined, detailErr: null } : x)),
		);
		fetchPlanRecordDetail(recordId)
			.then((detail) =>
				setTimeline((t) => t.map((x) => (x.recordId === recordId ? { ...x, detail } : x))),
			)
			.catch((e) =>
				setTimeline((t) =>
					t.map((x) =>
						x.recordId === recordId ? { ...x, detail: null, detailErr: planErrorMessage(e) } : x,
					),
				),
			);
	}

	// 切换页签回来时工作区高度重算一次（工具条内容变化由 ResizeObserver 兜）
	// biome-ignore lint/correctness/useExhaustiveDependencies: 只在轮数变化时重算
	useEffect(() => {
		window.dispatchEvent(new Event("resize"));
	}, [timeline.length]);

	const customerDisabled = cat.isError || (!cat.isLoading && !seedCustomers.length);
	const customerReason = cat.isError
		? `客户列表没加载出来：${planErrorMessage(cat.error)}`
		: "没有客户种子数据";

	const inputFooter = (
		<div className="space-y-1.5">
			{err ? (
				<div
					className="flex items-center gap-2 px-1 text-xs text-brand-danger"
					data-testid="plan-send-error"
				>
					<span className="min-w-0 flex-1">{err}</span>
					{failedMsg ? (
						<Button type="button" variant="outline" size="sm" onClick={() => send(failedMsg)}>
							重试
						</Button>
					) : null}
				</div>
			) : null}
			<div className="flex gap-2">
				<Input
					className="flex-1"
					placeholder={
						hasKey ? "输入用户消息，回车发送…" : "需配置 LLM key（服务器 .env 的 LLM_API_KEY）"
					}
					value={msg}
					disabled={!hasKey || busy}
					onChange={(e) => setMsg(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") send();
					}}
					data-testid="plan-ask-input"
				/>
				<Button
					type="button"
					disabled={!hasKey || busy}
					title={hasKey ? undefined : "需配置 LLM key"}
					onClick={() => send()}
				>
					{turn.isPending ? "运行中…" : "发送"}
				</Button>
			</div>
		</div>
	);

	const turns: DrawerTurn[] = timeline.map(toChatTurn);
	const allTurns: DrawerTurn[] = pendingMsg
		? [...turns, { msg: pendingMsg, pending: true }]
		: turns;
	const fIdx = Math.min(focus, Math.max(0, allTurns.length - 1));
	const fEntry = timeline[fIdx];
	const trace =
		pendingMsg && fIdx === timeline.length
			? { detail: null, pending: true }
			: {
					detail: fEntry?.detail ?? null,
					loading: !!fEntry && fEntry.detail === undefined,
					error:
						fEntry && fEntry.detail === null ? (fEntry.detailErr ?? "这一轮的轨迹没拉到") : null,
					onRetry: fEntry ? () => loadDetail(fEntry.recordId) : undefined,
				};

	return (
		<>
			<Card data-testid="plan-block-toolbar" ref={toolbarRef}>
				<CardContent className="space-y-3 p-6">
					<div className="flex flex-wrap items-center gap-3">
						<span className="flex items-center gap-1.5 text-xs text-text-secondary">
							<span>客户身份</span>
							<StyledSelect
								aria-label="客户身份"
								value={customer}
								disabled={customerDisabled}
								onChange={(e) => reset(e.target.value)}
								title={
									customerDisabled
										? customerReason
										: "切换客户会重开会话（身份在会话首轮固定，决定查谁的单）"
								}
							>
								{seedCustomers.length ? (
									seedCustomers.map((c) => (
										<option key={c.customer_id} value={c.customer_id}>
											{c.customer_id} · {c.name}（{c.order_count}单）
										</option>
									))
								) : (
									<option value={customer}>{customer}</option>
								)}
							</StyledSelect>
							{cat.isError ? (
								<span className="text-brand-danger" data-testid="plan-catalog-error">
									{customerReason}
								</span>
							) : null}
						</span>
						<Button type="button" variant="outline" size="sm" onClick={() => reset()}>
							新会话
						</Button>
						<PlanStatus view={last?.plan_view ?? null} />
					</div>
				</CardContent>
			</Card>
			<div data-testid="plan-block-workfill">
				<div
					ref={work.ref}
					data-testid="plan-workfill"
					style={{ height: work.height }}
					className="overflow-hidden rounded-lg border border-surface-border bg-surface-card"
				>
					<PlanTraceColumns
						turns={allTurns}
						focus={fIdx}
						onFocus={setFocus}
						trace={trace}
						customerId={customer}
						chatFooter={inputFooter}
						chatEmptyHint={
							hasKey
								? "选客户 → 发一句多诉求的话。例：「帮我把订单 2003 取消了，顺便看看 2002 的物流到哪了」——逐件办，要确认时只问一个。"
								: "需配置 LLM key 才能真跑。"
						}
						className="h-full"
					/>
				</div>
			</div>
		</>
	);
}
